import { FC, useCallback, useEffect, useMemo } from 'react'

import { Box, Button, Menu, Portal, Span } from '@chakra-ui/react'
import { ChevronDownIcon } from '@editor/assets/icons'
import { DIAMETERS, EDITOR_TOOLS, PanelType } from '@editor/config/constants'
import { INPUT_GROUP_STYLES } from '@editor/config/styles'
import { useEditorContext } from '@editor/contexts/Editor'
import CollapsePanel from '@editor/pages/projects/editor/infoPanels/components/CollapsePanel'
import { toggleCollapsedPanel } from '@editor/pages/projects/editor/store/editor'
import { findDiameterKeyByValue } from '@editor/services/Util'
import { RootState, useAppDispatch } from '@editor/store/app'
import { toggleSelectedElementIds } from '@editor/store/tool'
import { useSelector } from 'react-redux'

import { setDiameter } from '../store'

const DiameterPanel: FC = () => {
  // Context
  const { selectedTool } = useEditorContext()

  // Store
  const dispatch = useAppDispatch()
  const baseDiameter = useSelector((state: RootState) => state.toolRebarDetection.baseDiameter)
  const workingDistanceLabels = useSelector((state: RootState) => state.toolRebarDetection.workingLines)
  const selectedElementIds = useSelector((state: RootState) => state.toolUtil.core.selectedElementIds)
  const collapsedPanel = useSelector((state: RootState) => state.editor.collapsedPanel)

  // Flags
  const isCollapsed = useMemo(() => collapsedPanel.includes(PanelType.Tool), [collapsedPanel])

  /**
   * Update the base diameter of the rebar and any rebar that its diameter has not been set yet.
   */
  const updateDiameter = useCallback(
    (diameter: number) => {
      dispatch(setDiameter(diameter))
    },
    [dispatch],
  )

  /**
   * Only 1 working label can be selected at a time
   */
  useEffect(() => {
    const workingIds = workingDistanceLabels.map((label) => label.id)
    const selectedLabels = selectedElementIds.filter((id) => workingIds.includes(id))
    if (selectedLabels.length > 1) {
      dispatch(
        toggleSelectedElementIds({ ids: selectedLabels.slice(0, -1).map((labelId) => labelId), selected: false }),
      )
    }
  }, [selectedElementIds, workingDistanceLabels, dispatch])

  if (selectedTool !== EDITOR_TOOLS.CYLINDER) {
    return null
  }

  return (
    <CollapsePanel
      title="鉄筋の直径指定"
      onChange={() => dispatch(toggleCollapsedPanel(PanelType.Tool))}
      isCollapsed={isCollapsed}
    >
      <Box data-testid="rebar-diameter-panel" className="inner-container" position="relative">
        <Menu.Root variant="editorPanel" positioning={{ placement: 'top-start', sameWidth: true }}>
          <Menu.Trigger asChild>
            <Button
              textAlign="left"
              fontWeight="normal"
              fontSize="xs"
              paddingX={2}
              height={7}
              width="100%"
              variant="toolbar"
              {...INPUT_GROUP_STYLES}
              borderColor={!baseDiameter ? 'red' : 'whiteAlpha.200'}
              justifyContent="space-between"
            >
              <Span>{baseDiameter ? `${findDiameterKeyByValue(baseDiameter)}（${baseDiameter || ''}）` : ''}</Span>
              <ChevronDownIcon />
            </Button>
          </Menu.Trigger>
          <Portal>
            <Menu.Positioner>
              <Menu.Content maxHeight="70vh" overflowY="auto">
                {Object.keys(DIAMETERS).map((d) => (
                  <Menu.Item
                    key={d}
                    value={d}
                    backgroundColor={baseDiameter === DIAMETERS[d] ? 'secondary.600' : undefined}
                    onClick={() => updateDiameter(DIAMETERS[d])}
                    fontSize="xs"
                  >
                    {d}（{DIAMETERS[d]}）
                  </Menu.Item>
                ))}
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Menu.Root>
      </Box>
    </CollapsePanel>
  )
}

export default DiameterPanel
